class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.options = [];
    this.selectedIndex = -1;
    this._focusedIndex = null;
    this._scrollRAF = null;
    this._sectionId = this.dataset.sectionId || 'default';
    this.nativePrimaryButton = null;
    this._boundClose = this.close.bind(this);
    this._boundOnScroll = this._onScroll.bind(this);
    this._boundNativeSync = this._syncNativeButtonState.bind(this);
    this._boundSyncBodyOffset = () => StickyAddToCart._syncBodyOffset();
  }

  connectedCallback() {
    requestAnimationFrame(() => {
      this._cacheDOM();
      this._extractOptions();
      this._buildDropdown();
      this._selectInitial();
      this._setupVisibility();
      this._setupBodyOffset();
      this._attachEvents();
      this._listenForChanges();
      this._syncNativeButtonState();
    });
  }

  disconnectedCallback() {
    this._io?.disconnect();
    this._offsetResizeObserver?.disconnect();
    this._offsetClassObserver?.disconnect();
    window.removeEventListener('resize', this._boundSyncBodyOffset, { passive: true });
    StickyAddToCart._syncBodyOffset();
    window.removeEventListener('scroll', this._boundOnScroll, { passive: true });
    window.removeEventListener('purchase-option:change', this._boundSync);
    window.removeEventListener('variant:change', this._boundNativeSync);
    document.removeEventListener('variant:change', this._boundNativeSync);
    document.removeEventListener('product-tab:change', this._boundNativeSync);
    document.removeEventListener('product-tab:switched', this._boundNativeSync);
    document.removeEventListener('purchase-option:activate', this._boundNativeSync);
    document.removeEventListener('click', this._boundDocClick, true);
    this.trigger?.removeEventListener('keydown', this._boundKeydown);
    this.dropdown?.removeEventListener('keydown', this._boundKeydown);
  }

  _cacheDOM() {
    this.trigger = this.querySelector('[data-sticky-trigger]');
    this.triggerLabel = this.querySelector('[data-sticky-trigger-label]');
    this.triggerPrice = this.querySelector('[data-sticky-trigger-price]');
    this.triggerSave = this.querySelector('[data-sticky-trigger-save]');
    this.dropdown = this.querySelector('[data-sticky-dropdown]');
    this.selectWrap = this.querySelector('[data-sticky-select]');
    this.overlay = this.querySelector('[data-sticky-overlay]');
    this.atcBtn = this.querySelector('[data-sticky-atc-btn]');
    this.btnLabel = this.querySelector('[data-sticky-btn-label]');
    this.liveRegion = this.querySelector('[data-sticky-live-region]');
    this.formInputs = {
      id: this.querySelector('input[name="id"]'),
      sellingPlan: this.querySelector('input[name="selling_plan"]'),
      quantity: this.querySelector('input[name="quantity"]'),
      months:
        this.querySelector('input[name="properties[_months]"]') ||
        this.querySelector('input[name="properties[_otp]"]'),
    };
  }

  _extractOptions() {
    this.options = [];
    const blockInputs = document.querySelectorAll(
      'purchase-options .subscription-option__input, purchase-options .onetime-option__input'
    );

    if (blockInputs.length) {
      const subs = [];
      let firstOtp = null;

      blockInputs.forEach((input) => {
        const isSubscription = input.classList.contains('subscription-option__input');
        const container = input.closest('.subscription-option, .onetime-option');
        const titleEl = container?.querySelector(
          '.subscription-option__title, .onetime-option__title'
        );
        const saveBadge = container?.querySelector(
          '.subscription-option__savings-badge, .onetime-option__savings-badge'
        );

        const planPrice = parseInt(input.dataset.planPrice || input.dataset.totalPrice || 0, 10);
        const comparePrice = parseInt(
          input.dataset.planComparePrice || input.dataset.comparePrice || 0,
          10
        );
        const savings = comparePrice > planPrice ? comparePrice - planPrice : 0;

        let titleText = titleEl?.textContent?.trim() || '';
        if (saveBadge && titleText.includes(saveBadge.textContent.trim())) {
          titleText = titleText.replace(saveBadge.textContent.trim(), '').trim();
        }

        const opt = {
          type: isSubscription ? 'subscription' : 'onetime',
          variantId: input.dataset.variantId || input.value,
          sellingPlan: input.dataset.sellingPlan || '',
          quantity: input.dataset.quantity || '1',
          months: input.dataset.months || input.dataset.quantity || '',
          planPrice,
          comparePrice,
          savings,
          title:
            titleText ||
            (isSubscription
              ? `${input.dataset.months || input.dataset.quantity} Month Delivery`
              : 'One Time Purchase'),
        };

        if (isSubscription) {
          subs.push(opt);
        } else if (!firstOtp) {
          opt.title = 'One Time Purchase';
          firstOtp = opt;
        }
      });

      subs.sort((a, b) => b.savings - a.savings);
      this.options = [...subs];
      if (firstOtp) this.options.push(firstOtp);
      return;
    }

    const metaLabels = document.querySelectorAll('product-delivery-option label.delivery-option');
    if (!metaLabels.length) return;

    const subs = [];
    let firstOtp = null;

    metaLabels.forEach((label) => {
      const isOtp = label.dataset.productOtp === 'true';
      const qty = parseInt(label.dataset.quantity || '1', 10);
      const fullPrice = parseInt(label.dataset.fullPrice || '0', 10);
      const price = parseInt(label.dataset.price || fullPrice, 10);
      const savings = fullPrice > price ? fullPrice - price : 0;
      const titleEl = label.querySelector('.option-title');
      const titleText =
        titleEl?.childNodes?.[0]?.textContent?.trim() ||
        label.dataset.sellingPlanName ||
        '';

      const opt = {
        type: isOtp ? 'onetime' : 'subscription',
        variantId: label.dataset.productId || '',
        sellingPlan: label.dataset.sellingPlan || '',
        quantity: String(qty),
        months: String(qty),
        planPrice: price * qty,
        comparePrice: fullPrice * qty,
        savings: savings * qty,
        title: titleText || (isOtp ? 'One Time Purchase' : `${qty} Month Delivery`),
      };

      if (!isOtp) {
        subs.push(opt);
      } else if (!firstOtp) {
        opt.title = 'One Time Purchase';
        firstOtp = opt;
      }
    });

    subs.sort((a, b) => b.savings - a.savings);
    this.options = [...subs];
    if (firstOtp) this.options.push(firstOtp);
  }

  _buildDropdown() {
    if (!this.dropdown) return;
    this.dropdown.innerHTML = '';

    this.options.forEach((opt, i) => {
      const el = document.createElement('div');
      el.className = 'sticky-atc__option';
      el.id = `sticky-atc-opt-${this._sectionId}-${i}`;
      el.dataset.index = i;
      el.setAttribute('role', 'option');
      el.setAttribute('tabindex', '-1');
      el.setAttribute('aria-selected', 'false');

      const label = `${opt.title}, ${this._formatMoney(opt.planPrice)}${
        opt.savings > 0 ? `, save ${this._formatMoney(opt.savings)}` : ''
      }`;
      el.setAttribute('aria-label', label);

      let metaHTML = '';
      if (opt.planPrice && opt.savings > 0) {
        metaHTML += `<span class="sticky-atc__option-price" aria-hidden="true">${this._formatMoney(opt.planPrice)}</span>`;
        metaHTML += `<span class="sticky-atc__option-save" aria-hidden="true">SAVE ${this._formatMoney(opt.savings)}</span>`;
      } else if (opt.planPrice) {
        metaHTML += `<span class="sticky-atc__option-price" aria-hidden="true">${this._formatMoney(opt.planPrice)}</span>`;
      }

      el.innerHTML = `<span class="sticky-atc__option-name">${opt.title}</span><span class="sticky-atc__option-meta">${metaHTML}</span>`;
      el.addEventListener('click', () => this._selectOption(i));
      this.dropdown.appendChild(el);
    });
  }

  _selectInitial() {
    if (!this.options.length) return;
    let idx = 0;

    const root = this._getActivePurchaseOptions();
    const checkedSub = root?.querySelector('.subscription-option--selected .subscription-option__input');
    const checkedOtp = root?.querySelector('.onetime-option--selected .onetime-option__input');
    const checked = checkedSub || checkedOtp;

    if (checked) {
      const vid = checked.dataset.variantId || checked.value;
      const sp = checked.dataset.sellingPlan || '';
      const foundIdx = this.options.findIndex((o) => o.variantId === vid && o.sellingPlan === sp);
      if (foundIdx !== -1) idx = foundIdx;
    }

    this._selectOption(idx, true, true);
  }

  _selectOption(index, silent = false, skipNativeSelectionSync = false) {
    if (index < 0 || index >= this.options.length) return;
    const opt = this.options[index];
    this.selectedIndex = index;

    if (this.triggerLabel) {
      this.triggerLabel.textContent = opt.title;
      this.triggerLabel.classList.remove('sticky-atc__trigger-label--placeholder');
    }
    if (this.triggerPrice) this.triggerPrice.textContent = opt.planPrice ? this._formatMoney(opt.planPrice) : '';
    if (this.triggerSave) this.triggerSave.textContent = opt.savings > 0 ? `SAVE ${this._formatMoney(opt.savings)}` : '';

    if (this.formInputs.id) this.formInputs.id.value = opt.variantId;
    if (this.formInputs.sellingPlan) this.formInputs.sellingPlan.value = opt.sellingPlan;
    if (this.formInputs.quantity) this.formInputs.quantity.value = opt.type === 'onetime' ? opt.quantity : '1';
    if (this.formInputs.months) {
      if (opt.type === 'subscription') {
        this.formInputs.months.name = 'properties[_months]';
        this.formInputs.months.value = opt.months;
      } else {
        this.formInputs.months.name = 'properties[_otp]';
        this.formInputs.months.value = opt.quantity;
      }
    }

    if (!skipNativeSelectionSync) this._syncMainSelection(opt);

    const activeId = `sticky-atc-opt-${this._sectionId}-${index}`;
    this.dropdown?.querySelectorAll('.sticky-atc__option').forEach((el, i) => {
      el.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
    this.trigger?.setAttribute('aria-activedescendant', activeId);

    if (this.liveRegion) {
      this.liveRegion.textContent = `${opt.title}, ${this._formatMoney(opt.planPrice)}${
        opt.savings > 0 ? `, save ${this._formatMoney(opt.savings)}` : ''
      } selected`;
    }

    this._syncNativeButtonState();
    if (!silent) this.close();
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    if (this.isOpen || !this.options.length || !this.selectWrap) return;
    this.isOpen = true;
    this._focusedIndex = null;
    this.selectWrap.classList.add('is--open');
    this.overlay?.classList.add('is--active');
    this.trigger?.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this._focusedIndex = null;
    this.selectWrap?.classList.remove('is--open');
    this.overlay?.classList.remove('is--active');
    this.trigger?.setAttribute('aria-expanded', 'false');
  }

  _setupVisibility() {
    const selector = this.dataset.anchorSelector || '.purchase-options';
    const anchor = document.querySelector(selector);
    if (!anchor) {
      this.classList.add('is--visible');
      StickyAddToCart._syncBodyOffset();
      return;
    }

    this._io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) this.classList.remove('is--visible');
      else this.classList.add('is--visible');
      StickyAddToCart._syncBodyOffset();
    });

    this._io.observe(anchor);
  }

  _setupBodyOffset() {
    this._offsetResizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => StickyAddToCart._syncBodyOffset());
    });
    this._offsetResizeObserver.observe(this);

    this._offsetClassObserver = new MutationObserver(() => {
      requestAnimationFrame(() => StickyAddToCart._syncBodyOffset());
    });
    this._offsetClassObserver.observe(this, { attributes: true, attributeFilter: ['class'] });

    window.addEventListener('resize', this._boundSyncBodyOffset, { passive: true });
    StickyAddToCart._syncBodyOffset();
  }

  static _syncBodyOffset() {
    const bars = [...document.querySelectorAll('sticky-add-to-cart')];
    let maxHeight = 0;

    bars.forEach((bar) => {
      const style = window.getComputedStyle(bar);
      if (style.display === 'none' || style.visibility === 'hidden') return;
      if (!bar.classList.contains('is--visible')) return;
      maxHeight = Math.max(maxHeight, bar.offsetHeight);
    });

    if (maxHeight > 0) {
      document.documentElement.style.setProperty('--sticky-atc-height', `${maxHeight}px`);
      document.body.classList.add('has--sticky-atc');
    } else {
      document.documentElement.style.setProperty('--sticky-atc-height', '0px');
      document.body.classList.remove('has--sticky-atc');
    }
  }

  _onScroll() {
    if (!this.isOpen) return;
    if (this._scrollRAF) cancelAnimationFrame(this._scrollRAF);
    this._scrollRAF = requestAnimationFrame(() => this.close());
  }

  _attachEvents() {
    this.trigger?.addEventListener('click', () => this.toggle());
    this.overlay?.addEventListener('click', this._boundClose);
    window.addEventListener('scroll', this._boundOnScroll, { passive: true });

    this._boundDocClick = (e) => {
      if (this.isOpen && !this.selectWrap?.contains(e.target)) this.close();
    };
    document.addEventListener('click', this._boundDocClick, true);

    this.atcBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this._handleStickyButtonClick();
    });

    this._boundKeydown = (e) => this._onKeydown(e);
    this.trigger?.addEventListener('keydown', this._boundKeydown);
    this.dropdown?.addEventListener('keydown', this._boundKeydown);

    window.addEventListener('variant:change', this._boundNativeSync);
    document.addEventListener('variant:change', this._boundNativeSync);
    document.addEventListener('product-tab:change', this._boundNativeSync);
    document.addEventListener('product-tab:switched', this._boundNativeSync);
    document.addEventListener('purchase-option:activate', this._boundNativeSync);
  }

  _onKeydown(e) {
    const { key } = e;
    if (!this.isOpen) {
      if (key === 'Enter' || key === ' ' || key === 'ArrowDown' || key === 'ArrowUp') {
        e.preventDefault();
        this.open();
        this._focusOption(this.selectedIndex >= 0 ? this.selectedIndex : 0);
      }
      return;
    }

    switch (key) {
      case 'ArrowDown':
        e.preventDefault();
        this._focusOption(Math.min((this._focusedIndex ?? this.selectedIndex) + 1, this.options.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._focusOption(Math.max((this._focusedIndex ?? this.selectedIndex) - 1, 0));
        break;
      case 'Home':
        e.preventDefault();
        this._focusOption(0);
        break;
      case 'End':
        e.preventDefault();
        this._focusOption(this.options.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (this._focusedIndex != null) this._selectOption(this._focusedIndex);
        else this.close();
        break;
      case 'Escape':
      case 'Tab':
        this.close();
        this.trigger?.focus();
        break;
    }
  }

  _focusOption(index) {
    if (index < 0 || index >= this.options.length) return;
    this._focusedIndex = index;
    const optEl = this.dropdown?.querySelector(`#sticky-atc-opt-${this._sectionId}-${index}`);
    optEl?.focus({ preventScroll: false });
    this.trigger?.setAttribute('aria-activedescendant', optEl?.id || '');
  }

  _listenForChanges() {
    this._boundSync = (e) => {
      const detail = e.detail || {};
      const idx = this.options.findIndex(
        (o) => String(o.variantId) === String(detail.variantId || '') && o.sellingPlan === (detail.sellingPlan || '')
      );
      if (idx !== -1 && idx !== this.selectedIndex) this._selectOption(idx, true, true);
      this._syncNativeButtonState();
    };
    window.addEventListener('purchase-option:change', this._boundSync);
  }

  _handleStickyButtonClick() {
    if (this.selectedIndex >= 0) this._syncMainSelection(this.options[this.selectedIndex]);
    this._syncNativeButtonState();

    if (this.nativePrimaryButton && !this._isNativeDisabled(this.nativePrimaryButton)) {
      this.nativePrimaryButton.click();
      return;
    }

    this._submitToCart();
  }

  _getActivePurchaseOptions() {
    const all = [...document.querySelectorAll('purchase-options')];
    if (!all.length) return null;
    const active = all.find((el) => {
      if (el.classList.contains('active')) return true;
      const parent = el.closest('[id^="purchase-option-"]');
      return !!(parent && parent.classList.contains('active'));
    });
    return active || all[0];
  }

  _syncMainSelection(opt) {
    const root = this._getActivePurchaseOptions();
    if (!root || !opt) return;

    const type = opt.type === 'subscription' ? 'subscription' : 'onetime';
    const tabButton = root.querySelector(`.purchase-options__tab-nav[data-purchase-type="${type}"]`);
    const tabInput =
      root.querySelector(`.purchase-options__tab-input[data-purchase-type="${type}"]`) ||
      root.querySelector(`.purchase-options__tab-input[value="${type}"]`);

    if (tabButton && !tabButton.classList.contains('active')) {
      tabButton.click();
    } else if (tabInput && !tabInput.checked) {
      tabInput.checked = true;
      tabInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const inputs = root.querySelectorAll('.subscription-option__input, .onetime-option__input');
    const match = [...inputs].find((input) => {
      const variantId = input.dataset.variantId || input.value;
      const sellingPlan = input.dataset.sellingPlan || '';
      const inputType = input.classList.contains('subscription-option__input') ? 'subscription' : 'onetime';
      return (
        inputType === type &&
        String(variantId) === String(opt.variantId) &&
        String(sellingPlan) === String(opt.sellingPlan || '')
      );
    });

    if (match) {
      match.checked = true;
      match.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  _findNativePrimaryButton() {
    const root = this._getActivePurchaseOptions();
    const contexts = [root, root?.closest('form'), document];
    const selector =
      '.purchase-summary__add-to-cart, .purchase-summary .waitlist-trigger-button, .purchase-summary [id^="waitlist-trigger-button-"]';
    const seen = new Set();
    const candidates = [];

    contexts.forEach((ctx) => {
      if (!ctx || !ctx.querySelectorAll) return;
      ctx.querySelectorAll(selector).forEach((el) => {
        if (!seen.has(el)) {
          seen.add(el);
          candidates.push(el);
        }
      });
    });

    if (!candidates.length) return null;
    return candidates.find((el) => this._isVisible(el)) || candidates[0];
  }

  _isVisible(el) {
    if (!el) return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  _isNativeDisabled(el) {
    if (!el) return true;
    if (el.matches('button, input') && el.disabled) return true;
    if (el.getAttribute('aria-disabled') === 'true') return true;
    if (el.classList.contains('disabled') || el.classList.contains('is-disabled')) return true;
    return false;
  }

  _normalizeText(text) {
    return String(text || '').replace(/\s+/g, ' ').trim();
  }

  _syncNativeButtonState() {
    if (!this.atcBtn) return;

    const nativeButton = this._findNativePrimaryButton();
    this.nativePrimaryButton = nativeButton || null;
    if (!nativeButton) return;

    const nativeTextNode = nativeButton.querySelector?.('.purchase-summary__button-text');
    const nextText = this._normalizeText(
      nativeTextNode ? nativeTextNode.textContent : nativeButton.textContent
    );
    if (this.btnLabel) {
      if (nativeTextNode && nativeTextNode.innerHTML) {
        this.btnLabel.innerHTML = nativeTextNode.innerHTML;
      } else if (nextText) {
        this.btnLabel.textContent = nextText;
      }
    }

    const nextAria = nativeButton.getAttribute('aria-label') || nextText;
    if (nextAria) this.atcBtn.setAttribute('aria-label', nextAria);

    const disabled = this._isNativeDisabled(nativeButton);
    this.atcBtn.disabled = disabled;
    this.atcBtn.classList.toggle('is--disabled', disabled);
  }

  _submitToCart() {
    if (this.selectedIndex < 0 || !this.formInputs.id?.value) return;
    const opt = this.options[this.selectedIndex];

    this.atcBtn.classList.add('is--loading');
    this.atcBtn.disabled = true;

    const body = {
      id: parseInt(this.formInputs.id.value, 10),
      quantity: parseInt(this.formInputs.quantity?.value || '1', 10),
    };
    if (this.formInputs.sellingPlan?.value) {
      body.selling_plan = parseInt(this.formInputs.sellingPlan.value, 10);
    }

    const props = {};
    if (opt.type === 'subscription' && opt.months) {
      props['_months'] = opt.months;
    } else if (opt.type === 'onetime' && opt.quantity) {
      props['_otp'] = opt.quantity;
    }

    const preorderShipping = window.preorderShippingProperty;
    const shippingInput = document.querySelector('[data-preorder-shipping-property]');
    const shippingValue = preorderShipping?.value || shippingInput?.value;
    if (shippingValue) {
      props['_shipping_date'] = shippingValue;
    }

    if (Object.keys(props).length) body.properties = props;

    let preAddCartProductIds = [];
    window.__pdpUpsellPopupPending = true;

    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        preAddCartProductIds = (cart.items || []).map((item) => Number(item.product_id || 0));

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [body] }),
        });
      })
      .then((r) => r.json())
      .then((data) => {
        if (data.status === 422) throw new Error(data.description || 'Unable to add to cart');

        const proceedToCheckout = this.atcBtn.dataset.proceedToCheckout === 'true';
        if (proceedToCheckout) {
          window.__pdpUpsellPopupPending = false;
          setTimeout(() => (window.location.href = '/checkout'), 300);
          return;
        }

        const pdpAddedDetail = this._dispatchPdpAddedEvent(data, preAddCartProductIds);
        window.__pdpUpsellPopupPending = false;
        if (!pdpAddedDetail?.deferCartDrawer) {
          if (typeof window.refreshCartDrawer === 'function') {
            window.refreshCartDrawer({ open: true });
          } else {
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }
        }
      })
      .catch((err) => {
        window.__pdpUpsellPopupPending = false;
        console.error('[StickyATC]', err.message);
      })
      .finally(() => {
        this.atcBtn.classList.remove('is--loading');
        this.atcBtn.disabled = false;
        this._syncNativeButtonState();
      });
  }

  _dispatchPdpAddedEvent(response, preAddCartProductIds = []) {
    if (!response) return null;

    const item = Array.isArray(response.items) ? response.items[0] : response;
    if (!item || !item.product_id) return null;

    const sellingPlan = item.selling_plan_allocation?.selling_plan?.id;

    const eventDetail = {
      productId: Number(item.product_id),
      variantId: Number(item.variant_id || item.id) || null,
      quantity: Number(item.quantity) || 1,
      sellingPlanId: sellingPlan ? String(sellingPlan) : null,
      lineItemKey: item.key || null,
      preAddCartProductIds,
      item,
    };

    document.dispatchEvent(new CustomEvent('arrae:pdp:added', { detail: eventDetail }));
    return eventDetail;
  }

  _formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);
